import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Products() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [products]);

  const filtered = products.filter((p) => {
    const matchesCat = cat === "All" || p.category === cat;
    const q = query.toLowerCase();
    const matchesQ = !q || p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q);
    return matchesCat && matchesQ;
  });

  return (
    <div data-testid="products-page">
      <section className="border-b border-border bg-secondary/40">
        <div className="container-xl py-14 md:py-20">
          <span className="eyebrow text-accent">Our Products</span>
          <h1 className="mt-3 text-4xl font-700 sm:text-5xl">Natural homeopathic remedies</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Explore our range of gentle, plant-based remedies crafted for everyday wellness.
          </p>

          <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1 md:max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="products-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search remedies..."
                className="input-field !pl-12"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="products-category-filter">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`category-${c}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    cat === c ? "bg-primary text-primary-foreground" : "bg-white text-foreground/70 ring-1 ring-border hover:bg-secondary"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-xl">
          {loading ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground" data-testid="products-empty">No products match your search.</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
              {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
